import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../config/api";

export default function MyListings({ onUpgrade }) {
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    const user_id = userData?.personaluid;
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const pageOptions = [10, 50, 100, 150, 200];


    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!user_id) {
            setLoading(false);
            return;
        }

        fetch(`${API_BASE}/api/get_user_listings.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) setListings(data.listings);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [user_id]);

    const openListing = (listinguid) => navigate(`/profile?listinguid=${listinguid}`);

    const goTocommandrev = (listinguid) => navigate(`/commentsreviews?listinguid=${listinguid}`);
    useEffect(() => {
        // Initialize tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(
            el => new window.bootstrap.Tooltip(el)
        );

        // Cleanup: Destroy tooltips when component unmounts
        return () => {
            tooltipList.forEach(t => t.dispose());
        };
    }, [listings]);

    // 🔍 SEARCH FILTER
    const filteredListings = listings.filter(item =>
        item.title.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()) ||
        item.mobile.includes(search)
    );

    // 📄 PAGINATION
    const totalPages = Math.ceil(filteredListings.length / itemsPerPage);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const paginatedListings = filteredListings.slice(startIndex, endIndex);
    const getPageNumbers = () => {
        const maxPagesToShow = 5;
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxPagesToShow - 1);

        if (end - start < maxPagesToShow - 1) {
            start = Math.max(1, end - maxPagesToShow + 1);
        }

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage]);
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [itemsPerPage, search, totalPages]);

    // BEAUTIFUL LOADER
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                <div className="spinner-box text-center">
                    <div className="pulse-loader"></div>
                    <p className="mt-3 text-secondary fw-semibold">Fetching your business listings...</p>
                </div>

                <style>
                    {`
                        .pulse-loader {
                            width: 60px;
                            height: 60px;
                            border-radius: 50%;
                            background: linear-gradient(135deg, #4a90e2, #5fb3ff);
                            animation: pulse 1.2s infinite ease-in-out;
                        }
                        @keyframes pulse {
                            0% { transform: scale(0.85); opacity: 0.7; }
                            50% { transform: scale(1); opacity: 1; }
                            100% { transform: scale(0.85); opacity: 0.7; }
                        }
                    `}
                </style>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">

            {/* PAGE HEADER */}
            <div
                className="rounded p-3 mb-3 shadow-sm"
                style={{
                    background: "linear-gradient(135deg, #e8f0ff, #fefefe)",
                    borderLeft: "6px solid #4a90e2"
                }}
            >

                <h5 className="fw-bold text-dark mb-1">My Listed Businesses</h5>
                <p className="text-secondary mb-0">
                    View, manage and upgrade your business listings.
                </p>
            </div>


            {/* NO LISTINGS MESSAGE */}
            {listings.length === 0 ? (
                <div className="alert alert-info shadow-sm p-3 fs-5 text-center">
                    You have not listed any business yet.
                </div>
            ) : (
                <div className="table-responsive shadow rounded">
                    <div className="card shadow-sm border-0">

                        {/* TABLE CARD HEADER */}
                        <div className="card-header bg-white border-bottom">
                            <div className="row align-items-center g-2">

                                {/* LEFT: PAGE SIZE */}
                                <div className="col-md-6 d-flex align-items-center gap-2">
                                    <span className="text-muted small">Show</span>

                                    <select
                                        className="form-select form-select-sm w-auto"
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {pageOptions.map(n => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>

                                    <span className="text-muted small">entries</span>
                                </div>

                                {/* RIGHT: SEARCH */}
                                <div className="col-md-6 d-flex justify-content-md-end align-items-center gap-2">
                                    <label className="text-muted small mb-0">Search:</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Business, email, phone..."
                                        value={search}
                                        onChange={(e) => {
                                            setSearch(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        style={{ width: "220px" }}
                                    />
                                </div>

                            </div>
                        </div>


                        {/* TABLE CARD BODY */}
                        <div className="card-body p-0">

                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead
                                        style={{
                                            background: "#f2f6ff",
                                            borderBottom: "2px solid #dbe5ff"
                                        }}
                                    >
                                        <tr>
                                            <th>#</th>
                                            <th>Business</th>
                                            <th>Location</th>
                                            <th>Contact</th>
                                            <th>Created</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedListings.map((item, index) => (
                                            <tr key={item.id} className="listing-row">

                                                <td className="fw-bold text-primary">
                                                    {(currentPage - 1) * itemsPerPage + index + 1}

                                                </td>

                                                <td>
                                                    <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                                                    <small className="text-muted">{item.email}</small>
                                                </td>

                                                <td className="text-muted">
                                                    📍 {item.province}, {item.district}
                                                </td>

                                                <td className="text-muted">
                                                    📞 {item.mobile}
                                                </td>

                                                <td className="text-muted small">{item.created_at}</td>

                                                <td>
                                                    {item.states == 1 ? (
                                                        <span className="badge bg-success">Approved</span>
                                                    ) : (
                                                        <span className="badge bg-warning text-dark">Pending</span>
                                                    )}
                                                </td>

                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-3">
                                                        <i
                                                            className="bi bi-eye-fill text-primary fs-5"
                                                            title="View"
                                                            onClick={() => openListing(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>

                                                        <i
                                                            className="bi bi-star-fill text-warning fs-5"
                                                            title="Upgrade"
                                                            onClick={() => onUpgrade(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>

                                                        <i
                                                            className="bi bi-chat-left-text-fill text-success fs-5"
                                                            title="Comments"
                                                            onClick={() => goTocommandrev(item.listinguid)}
                                                            style={{ cursor: "pointer" }}
                                                        ></i>
                                                    </div>
                                                </td>

                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                        <div className="card-footer bg-white border-top">
                            <div className="row align-items-center g-2">

                                {/* LEFT: INFO */}
                                <div className="col-md-6 text-muted small">
                                    Showing {startIndex + 1} to{" "}
                                    {Math.min(endIndex, filteredListings.length)} of{" "}
                                    {filteredListings.length} entries
                                </div>

                                {/* RIGHT: PAGINATION */}
                                {totalPages > 1 && (
                                    <div className="col-md-6 d-flex justify-content-md-end">
                                        <nav>
                                            <ul className="pagination pagination-sm mb-0">

                                                <li className={`page-item ${currentPage === 1 && "disabled"}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => p - 1)}
                                                    >
                                                        Prev
                                                    </button>
                                                </li>

                                                {getPageNumbers().map(p => (
                                                    <li key={p} className={`page-item ${p === currentPage && "active"}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => setCurrentPage(p)}
                                                        >
                                                            {p}
                                                        </button>
                                                    </li>
                                                ))}

                                                <li className={`page-item ${currentPage === totalPages && "disabled"}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => setCurrentPage(p => p + 1)}
                                                    >
                                                        Next
                                                    </button>
                                                </li>

                                            </ul>
                                        </nav>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>

                </div>
            )}

            {/* ROW HOVER EFFECT */}
            <style>
                {`
                    .listing-row:hover {
                        background: #f7faff !important;
                        cursor: pointer;
                        transition: 0.2s ease-in-out;
                    }

                    table {
                        border-radius: 12px !important;
                        overflow: hidden;
                    }
                `}
            </style>
        </div>
    );
}
